'use client';

import {
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ctct/summit-core';
import type * as SelectPrimitive from '@radix-ui/react-select';
import { type ComponentProps, type FocusEvent, type ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { type InputOpts } from '../../types';

interface DropdownItem {
  label: string | ReactNode;
  value: string;
  isDisabled?: boolean;
}

const Dropdown = ({
  field: { name, value, onChange, onBlur: handleFieldBlur },
  emptyValue,
  onBlur: handleBlur,
  onValueChange: handleValueChange,
  placeholder,
  container,
  items = [],
  triggerClasses,
  ...selectProps
}: {
  field: {
    name: string;
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
  };
  emptyValue?: DropdownItem;
  container?: ComponentProps<typeof SelectPrimitive.Portal>['container'];
  onBlur?: (e: FocusEvent) => void;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  items?: DropdownItem[];
  triggerClasses?: string;
} & ComponentProps<typeof Select>) => {
  const handleChange = (value: string) => {
    if (value === emptyValue?.value) onChange('');
    else onChange(value);
    handleValueChange?.(value);
  };
  const handleTriggerBlur = (e: FocusEvent) => {
    handleFieldBlur();
    handleBlur?.(e);
  };
  const selectedLabel =
    items.find((item) => item.value === String(value))?.label ?? emptyValue?.label ?? undefined;
  const triggerAriaLabel =
    placeholder && typeof selectedLabel === 'string'
      ? `${placeholder}, ${selectedLabel}`
      : placeholder;

  return (
    <Select
      {...selectProps}
      onValueChange={(value: string) => handleChange(value)}
      name={name}
      value={String(value)}
    >
      <SelectTrigger
        className={cn('r:w-full', triggerClasses)}
        onBlur={handleTriggerBlur}
        aria-label={triggerAriaLabel}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent onBlur={handleBlur} container={container}>
        {emptyValue && (
          <SelectItem key={emptyValue.value} value={emptyValue.value}>
            {emptyValue.label}
          </SelectItem>
        )}
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value} disabled={item.isDisabled}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const DropdownInput = ({
  formKey,
  registerOpts,
  items = [],
  emptyValue,
  onBlur: handleBlur,
  onValueChange: handleValueChange,
  placeholder,
  container,
  triggerClasses,
  ...selectProps
}: InputOpts
  & ComponentProps<typeof Select> & {
    onBlur?: (e: FocusEvent) => void;
    placeholder?: string;
    items?: DropdownItem[];
    emptyValue?: DropdownItem;
    container?: ComponentProps<typeof SelectPrimitive.Portal>['container'];
    triggerClasses?: string;
  }) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={formKey}
      rules={registerOpts}
      render={({ field }) => (
        <Dropdown
          field={field}
          emptyValue={emptyValue}
          onBlur={handleBlur}
          onValueChange={handleValueChange}
          placeholder={placeholder}
          items={items}
          container={container}
          triggerClasses={triggerClasses}
          {...selectProps}
        />
      )}
    />
  );
};

export { DropdownInput, type DropdownItem };
